import { isValidZipCode, validateData } from './DataValidation';

import {
  DEFAULT_EVENT_PROPERTIES,
  DEFAULT_RULE_PROPERTIES,
  DEFAULT_REQUIREMENT_PROPERTIES,
  EVENTS,
} from '../data/Constants';

const defaultRuleProperties = DEFAULT_RULE_PROPERTIES;
const defaultRequirementProperties = DEFAULT_REQUIREMENT_PROPERTIES;

// Create the default NOT NULL rule for re-use.
export const RULE_NOT_NULL = createRule('NOT NULL', null);

/**
 * Find the event within the events object specified from the eid (event id)
 * @param {string} eid
 * @param {object} events
 * @returns
 */
export const filterEvent = (eid, events) =>
  events.filter((e) => e.id === eid)[0];

/**
 * Create a rule using this standardized template
 * @param {string} evaluate                The value from validate the rule against
 *
 * @param {string} nextEvent               The next event that the rules should send
 *                                         the app to if the evaluate rule passes
 *
 * @param {object} options                 Collection of optional settings for the rule
 *
 * @param {boolean} options.isFollowUp     Is this rule a follow up only, it makes
 *                                         sure (by default) that the value is not
 *                                         null and ignores the screen rules and
 *                                         evaluates the rest of the rules in the
 *                                         set.
 *
 * @param {object} options.followUpRule    use createFollowUpRule() to create these
 *                                         rules.  Use this for more detailed event
 *                                         follow-up questions where you need to ask
 *                                         an additional follow-up question only if
 *                                         a specific condition has been met.
 *
 * @param {boolean} options.voidIfFilled   Ignores the rule's next screen if that
 *                                         screen has already been viewed and filled
 *                                         out.
 *                                         NOTE: This option is ignored if you only
 *                                         provide one default rule on the event.
 * @returns
 *
 */
export function createRule(evaluate, nextEvent, options = {}) {
  return {
    ...defaultRuleProperties,
    default: evaluate === '*' ? true : false,
    evaluate: evaluate === '*' ? null : evaluate,
    nextEvent,
    options: { ...options },
  };
}

/**
 * Automatically sets the options to an isFollowUp to avoid typing that every
 * time.
 * @param {string} evaluate
 * @param {string} nextEvent
 */
export function createFollowUpRule(evaluate, nextEvent) {
  return createRule(evaluate, nextEvent, { isFollowUp: true });
}

/**
 * Create a requirement using this standardized template.
 * @param {string} requiredEvent
 * @param {string} value
 * @returns
 */
export function createRequirement(requiredEvent, value) {
  return {
    ...defaultRequirementProperties,
    requiredEvent: requiredEvent,
    value: value,
  };
}

/**
 * Evaluate if the event passed needs to be reset or not
 * @param {string} eventId
 * @param {object} events
 * @returns boolean
 */
export const eventNeedsReset = (eventId, events) => {
  // Passed as an object/array
  if (typeof events === 'object') {
    for (const i in events) {
      if (events[i] === eventId) return true;
    }
  }

  // Passed as string
  else if (events === eventId) return true;

  // Not found
  return false;
};

/**
 * Evaluates all of the events and determines which event to return.
 * @param {object} events
 * @returns event object
 */
export const checkEventRules = (events) => {
  // Start the process by evaluating the first event
  const nextEvent = evaluateEventRules(events[0].id, events);
  // console.log("checkEventRules nextEvent", nextEvent);
  return nextEvent;
};

/**
 * Recursive function that evaluatates all of the events and determines
 * which event is next.
 * @param {string} eventId
 * @param {object} events
 * @returns event id string
 */
const evaluateEventRules = (eventId, events, loops = 0) => {
  const event = filterEvent(eventId, events);
  // console.log(`Event ID: '${eventId}': evaluateEventRules ${loops}`, {event, events});

  const { id, rules, requirements } = event;
  loops += 1;

  if (loops > 50) {
    throw Error(`Event ID: '${eventId}': > 50 attempts evaluating rules.`);
  }

  // Verify that this current requirement set passes
  const eventPassedRequirements = evaluateEventRequirements(
    requirements,
    events,
  );

  // event is set for review, return the id as next
  if (eventNeedsAttention(event) && eventPassedRequirements) {
    // console.log(`Event ID: '${eventId}': evaluateEventRules ${id}: event needs attention`);
    return id;
  } else {
    // console.log(`Event ID: '${eventId}': evaluateEventRules ${id}: else...`, { review, viewed, eventPassedRequirements });
    const { defaultRules, checkRules } = verifyAndPrepRules(rules);

    // Loop through the checkRules to see if any pass
    for (const rule of checkRules) {
      // console.log(`Event ID: '${eventId}': evaluateEventRules checking checkRules`, rule);
      // console.log("checking checkRules", rule);
      // Rule passed, use this to evaluate the next rule.
      if (evaluateEventRule(event, rule)) {
        // console.log(`Event ID: '${eventId}': evaluateEventRules rule passed ${id} rule`, rule);

        // Expand the available rule options that need to be evaluated
        const { isFollowUp, followUpRule, voidIfFilled } = rule.options;

        // console.log(`Event ID: '${eventId}': rule options`, rule.options);

        /**
         * This next part is meant for evaluating follow-up questions
         * primarily.  This is used so that if the question has already
         * been asked and answered, ignore it so that we do not ask it
         * again and move on to the next follow-up question.
         */
        if (isFollowUp) {
          // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp rule`, rule);
          // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp event`, event);
          // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp followUpRule`, followUpRule);

          // Filter out the next event
          const followUpEvent = filterEvent(rule.nextEvent, events);
          // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp followUpEvent`, followUpEvent);

          if (
            typeof followUpRule !== 'undefined' &&
            evaluateEventRule(followUpEvent, followUpRule)
          ) {
            // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp returning 1`, followUpRule.nextEvent);

            const followUpEventCheck = filterEvent(
              followUpRule.nextEvent,
              events,
            );
            if (!evaluateEventRule(followUpEventCheck, RULE_NOT_NULL)) {
              let rulePassed = true;
              if (
                voidIfFilled &&
                !validateFlag_voidIfFilled(
                  filterEvent(followUpRule.nextEvent, events),
                )
              )
                rulePassed = false;
              if (rulePassed) return followUpRule.nextEvent;
            }
          }

          // See how the not null rule evaluates against the next event
          const ignoreRulePassed = evaluateEventRule(
            followUpEvent,
            RULE_NOT_NULL,
          );
          // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp without followUpRule ignoreRulePassed`, ignoreRulePassed);
          // Return the next event only if the next event is NULL (not filled out)

          if (!ignoreRulePassed) {
            // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp returning 2`, rule.nextEvent);
            let rulePassed = true;
            if (
              voidIfFilled &&
              !validateFlag_voidIfFilled(filterEvent(rule.nextEvent, events))
            )
              rulePassed = false;
            if (rulePassed) return rule.nextEvent;
          }
        }

        // Else, evaluate the next event in the line.
        else {
          // console.log(`Event ID: '${eventId}': evaluateEventRules isFollowUp else, '${isFollowUp}'`, rule);
          let rulePassed = true;
          if (
            voidIfFilled &&
            !validateFlag_voidIfFilled(filterEvent(rule.nextEvent, events))
          )
            rulePassed = false;
          if (rulePassed)
            return evaluateEventRules(rule.nextEvent, events, loops);
        }
      }
    }

    // Process the default rules
    for (const defaultRule of defaultRules) {
      // console.log(`Event ID: '${eventId}': evaluateEventRules checking defaultRules`, defaultRule);
      // Expand the available rule options that need to be evaluated
      const { voidIfFilled } = defaultRule.options;

      let flagsPassed = true;
      if (
        voidIfFilled &&
        !validateFlag_voidIfFilled(filterEvent(defaultRule.nextEvent, events))
      )
        flagsPassed = false;
      // console.log(`Event ID: '${eventId}': evaluateEventRules defaultRules flagsPassed'`, flagsPassed);
      if (flagsPassed)
        return evaluateEventRules(defaultRule.nextEvent, events, loops);
    }

    // error, no rules passed and no default rule.  Returning the current ID
    // console.log(`Event ID: '${eventId}': evaluateEventRules should not get here`, { eventId, events, loops });
    return id;
  }
};

/**
 * Evaluate the event requirements
 * @param {object} requirements
 * @param {object} events
 * @returns boolean
 */
const evaluateEventRequirements = (requirements, events) => {
  // No requirements, pass back true automatically
  // console.log("evaluateEventRequirements", { requirements, events });
  if (
    typeof requirements !== 'object' ||
    (typeof requirements === 'object' && requirements.length === 0)
  ) {
    // console.log("evaluateEventRequirements no requirements");
    return true;
  }

  // Loop through the requirements
  for (const i in requirements) {
    const { requiredEvent, value } = requirements[i];
    const event = filterEvent(requiredEvent, events);

    // If the value was passed as an array, check that it's ONE of
    // those values.  This is an "or" requirement.
    if (Array.isArray(value)) {
      let reqGroupPassed = false;

      for (const reqValue of value) {
        if (evaluateRequirementRule(event, reqValue)) {
          reqGroupPassed = true;
          break;
        }
      }

      // Return false if the rule does not pass (requirement not met)
      if (!reqGroupPassed) {
        // console.log(`Event ID: '${event.id}': evaluateEventRequirements requirements not met`);
        return false;
      }
    } else {
      // Return false if the rule does not pass (requirement not met)
      if (!evaluateRequirementRule(event, value)) {
        // console.log(`Event ID: '${event.id}': evaluateEventRequirements requirements not met`);
        return false;
      }
    }
  }

  // Return true if all requirements have been met
  // console.log("evaluateEventRequirements requirements met");
  return true;
};

/**
 *
 * @param {object} event - event object
 * @param {(Array|string)} value - requirement value
 * @returns {boolean}
 */
const evaluateRequirementRule = (event, value) => {
  // Create the rule to test for the requirement
  const rule = createRule(value, null);

  // Evaluate the rule against the event
  return evaluateEventRule(event, rule, true);
};

/**
 * Compare two different strings
 * @param {string} value1 - first string to compare
 * @param {string} value2 - second string to compare
 * @returns {boolean} - the result of the string comparison
 */
const compareStrings = (value1, value2) => {
  return `${value1}`.toLowerCase() === `${value2}`.toLowerCase();
};

/**
 * Evaluate the current event passed against the rule
 * @param {object} event
 * @param {object} rule
 * @param {boolean} debug
 * @returns boolean
 */
export const evaluateEventRule = (event, rule) => {
  const { id, result } = event;
  const { evaluate } = rule;
  // console.log(`Event ID: '${id}': evaluateEventRule evaluating event rule: ${id}`, result, evaluate);

  // Evaluate the rule to check if the event is NOT NULL.
  if (evaluate === 'NOT NULL') {
    // console.log(`Event ID: '${id}': evaluateEventRule evaluating NOT NULL`);
    return result === null || result === '' ? false : true;
  }

  // If the event result is an event object and not null
  else if (typeof result === 'object' && result !== null) {
    // console.log(`Event ID: '${id}': evaluateEventRule result is object`, typeof result);

    // Return false if the array is empty
    if (result.length === 0) {
      return false;
    }

    // Run through the options and see if one fits
    for (const i in result) {
      const currentResult = result[i];

      if (compareStrings(currentResult, evaluate)) {
        // console.log(`Event ID: '${id}': evaluateEventRule rule passed: evaluate "${evaluate}" against result "${currentResult}"`);
        return true;
      } else {
        if (typeof currentResult === 'object') {
          // this is for selected objects.  If we do have an object, default to using id
          // if you don't have id at this point, something is wrong, so lets freak out a little
          if (typeof currentResult.id === 'undefined') {
            throw Error(
              `Event ID: '${id}': The object ${currentResult} does not have the required id property to evaluate this rule`,
            );
          }

          if (compareStrings(currentResult.id, evaluate)) {
            return true;
          }
        }
      }
    }
    // default, return false
    return false;
  }

  // Else, result is not an object, but most likely a string or integer.
  else {
    // console.log(`Event ID: '${id}': evaluateEventRule rule: evaluate "${evaluate}" against result "${result}"`);
    if (compareStrings(result, evaluate)) return true;
    else return false;
  }
};

/**
 * The patient's earlier ZIP_CODE answer if it is a valid 5-digit zip, else ''.
 * @param {array} events
 * @returns {string}
 */
export const getPriorZipCode = (events) => {
  const zip = events?.find((ev) => ev.id === EVENTS.ZIP_CODE)?.result;
  return typeof zip === 'string' && isValidZipCode(zip) ? zip : '';
};

/**
 * Clean the events object and return just what the API needs.
 * @param {array} events
 * @param {string} patientOrOther
 * @returns array
 */
export const cleanEventsObjectForAPI = ({
  events,
  patientOrOther,
  doNotScrubResult = false,
}) => {
  return events
    .filter((event) => event.result !== null && event.result !== '')
    .map((event) => {
      const { id, result: rawResult, title: rawTitle } = event;
      const title = filterEventTitle(patientOrOther, rawTitle);
      const result =
        doNotScrubResult === true
          ? rawResult
          : transformEventResult(id, rawResult);
      return { id, title, result };
    });
};

/**
 * Transform the title (object or string) and return the expected title
 * @param {string} patientOrOther
 * @param {string/object} title
 * @returns string
 */
export const filterEventTitle = (patientOrOther, title) => {
  if (typeof title === 'string') return title;
  else if (typeof title[patientOrOther] !== 'undefined')
    return title[patientOrOther];
  else return 'error, undefined event title';
};

/**
 * Transforms the result based off of the event type (id)
 * @param {int} id
 * @param {string/object} result
 * @returns scaler
 */
const transformEventResult = (id, result) => {
  const { PURPOSE, LOCATION, PROVIDER } = EVENTS;

  let returnField = '';

  if (id === PURPOSE) returnField = 'PurposeId';
  else if (id === LOCATION) returnField = 'LocationId';
  else if (id === PROVIDER) returnField = 'ProviderId';

  if (returnField === '') return result;
  else {
    return result.map((itm) => {
      // Return the expected field, or the full object if it doesn't
      // exist for whatever reason.
      return typeof itm[returnField] === 'undefined' ? itm : itm[returnField];
    });
  }
};

/**
 * Verify the rules to make sure that they pass basic verification checks. Return
 * the default rules and check rules separately.
 * @param {array} rules - The array of rules to verify
 * @returns
 */
const verifyAndPrepRules = (rules) => {
  // Separate the default rules and all the other rules
  const defaultRules = rules.filter((rule) => rule.default);
  const checkRules = rules.filter((rule) => !rule.default);

  // Make sure there is only one default event rule
  if (defaultRules.length < 1)
    throw Error(
      `There must be one default rule per event, found '${defaultRules.length}'.`,
    );

  // Filter out the default rules that have options set on them.
  const basicDefaultRules = defaultRules.filter((r) => {
    const { options } = r;

    // Validate that options is an object
    validateData(options, 'object');

    // Return true/false based on if the options object has any entries or not.
    return Object.keys(options).length === 0 ? true : false;
  });

  // Throw an error if there are no basic default rules with no options set.
  if (basicDefaultRules.length === 0) {
    throw Error('There must be at least one default rule with no options set');
  }

  return { defaultRules, checkRules, rules };
};

/**
 * Validation for the voidIfFilled flag.
 * Returns true if the event needs to be viewed or not.
 * @param {object} event
 * @returns {boolean}
 */
const validateFlag_voidIfFilled = (event) => {
  // console.log("validateFlag_voidIfFilled", event);
  return eventNeedsAttention(event);
};

/**
 * Return true if the event object needs attention, false if it does not.
 * @param {object} event
 * @returns {boolean}
 */
const eventNeedsAttention = (event) => {
  // console.log("eventNeedsAttention", event);
  const { viewed, review, result } = event;

  // If the event has been viewed and not set for review, return false.
  if (viewed && !review) return false;
  // If the result is an empty string, return false
  else if (
    validateData(result, 'string', false) &&
    (result === null || result === '')
  ) {
    return false;
  }

  // if the result is an empty array, return false
  else if (validateData(result, 'array', false) && result.length <= 0) {
    return false;
  }

  // result is an empty object, return false.
  else if (
    validateData(result, 'object', false) &&
    Object.keys(result).length <= 0
  ) {
    return false;
  }

  // Else, return true
  else return true;
};
